import React from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import { useFormatters } from '../../../utils/format';
import {
  getBenefitsPackage,
  groupBenefitsByCategory,
  CATEGORY_ORDER,
} from '../../../data/benefitsPackages';

/**
 * BenefitsBreakdown
 * --------------------------------------------------------------------------
 * Shows the worker the full picture of their compensation package, not
 * just base + variable, but the monetary value of every benefit. This is
 * what Article 7 information rights look like at the comp-detail level:
 * if you don't know what your benefits are worth, you can't know if your
 * pay is fair compared with peers.
 *
 * Tenant-aware: each scenario shows the package appropriate to its
 * country and industry. The Total Benefits Value summed here is what
 * the parent CompensationOverview adds onto base + variable to build a
 * realistic Total Compensation figure.
 *
 * Each item shows: icon, label, monetary value (employer-cost equivalent
 * per year). Items grouped by category (health, retirement, mobility, etc.)
 * so the worker can see the structure of their package, not just a list.
 */

const CATEGORY_COLORS = {
  health:      { bg: 'bg-rose-50',    border: 'border-rose-200',    icon: 'text-rose-600',    titleKey: 'employeePortal.benefitsPkg.catHealth' },
  retirement:  { bg: 'bg-amber-50',   border: 'border-amber-200',   icon: 'text-amber-600',   titleKey: 'employeePortal.benefitsPkg.catRetirement' },
  mobility:    { bg: 'bg-sky-50',     border: 'border-sky-200',     icon: 'text-sky-600',     titleKey: 'employeePortal.benefitsPkg.catMobility' },
  protection:  { bg: 'bg-slate-50',   border: 'border-slate-200',   icon: 'text-slate-600',   titleKey: 'employeePortal.benefitsPkg.catProtection' },
  equity:      { bg: 'bg-emerald-50', border: 'border-emerald-200', icon: 'text-emerald-600', titleKey: 'employeePortal.benefitsPkg.catEquity' },
  lifestyle:   { bg: 'bg-violet-50',  border: 'border-violet-200',  icon: 'text-violet-600',  titleKey: 'employeePortal.benefitsPkg.catLifestyle' },
};

const BenefitsBreakdown = ({ tenantId }) => {
  const { t } = useTranslation();
  const { eur } = useFormatters();

  const pkg = getBenefitsPackage(tenantId);
  const grouped = groupBenefitsByCategory(pkg.items);
  const total = pkg.items.reduce((s, i) => s + (i.annualValueEur || 0), 0);

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-border bg-gradient-to-r from-primary/5 to-transparent">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
              <Icon name="Gift" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {t('employeePortal.benefitsPkg.title')}
              </h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                {t('employeePortal.benefitsPkg.subtitle')}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">
              {t('employeePortal.benefitsPkg.totalAnnualValue')}
            </p>
            <p className="text-2xl font-bold text-primary tabular-nums">{eur(total)}</p>
            <p className="text-xs text-muted-foreground">
              {t('employeePortal.benefitsPkg.perYearEmployerCost')}
            </p>
          </div>
        </div>
      </div>

      {/* Category groups */}
      <div className="p-6 space-y-5">
        {CATEGORY_ORDER.filter((cat) => grouped[cat]?.length > 0).map((cat) => {
          const items = grouped[cat];
          const categoryStyle = CATEGORY_COLORS[cat];
          const categoryTotal = items.reduce((s, i) => s + (i.annualValueEur || 0), 0);

          return (
            <div key={cat}>
              <div className="flex items-center justify-between mb-2.5">
                <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t(categoryStyle.titleKey)}
                </p>
                <p className="text-xs font-medium text-muted-foreground tabular-nums">
                  {eur(categoryTotal)}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-md border ${categoryStyle.bg} ${categoryStyle.border}`}
                  >
                    <Icon name={item.icon} size={18} className={`${categoryStyle.icon} shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground leading-tight break-words hyphens-auto">
                        {t(item.i18nKey)}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-foreground tabular-nums whitespace-nowrap">
                      {eur(item.annualValueEur)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footnote, Art. 7 framing */}
      <div className="px-6 pb-5 pt-1">
        <div className="flex items-start gap-2 text-xs text-muted-foreground border-t border-border pt-4">
          <Icon name="Info" size={14} className="shrink-0 mt-0.5" />
          <span>{t('employeePortal.benefitsPkg.footnote')}</span>
        </div>
      </div>
    </div>
  );
};

export default BenefitsBreakdown;
